import { useState } from 'react';
import type { DecorMatchResponseItem } from '@pomoshnik/shared';

/**
 * Список 5 карточек-результатов после photo-match (Экран 2.2.6 в PRD).
 * Презентационный компонент — показывает уже отсортированные backend'ом по
 * deltaE76 (ascending) карточки. Само значение ΔE на карточке НЕ показывается
 * (решение Aibek 2026-06-01 — «лишний подтекст»): пользователю показываем только
 * название + бренд; сортировка по близости остаётся серверной.
 *
 * Фото-2 (PR-DATA4): реальное фото декора из R2 через signed-URL (`imageUrls`
 * map `key→url`). Нет ключа / нет url / onError → плейсхолдер с инициалом бренда.
 */
export interface MatchResultsProps {
  items: readonly DecorMatchResponseItem[];
  /** key (`primaryPhotoKey`) → signed url. Контейнер передаёт из useDecorImageUrls. */
  imageUrls?: Record<string, string>;
  onItemClick: (globalId: string) => void;
}

export function MatchResults({ items, imageUrls, onItemClick }: MatchResultsProps) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted text-base m-0">Найдено похожих: {items.length}</p>
      <div className="flex flex-col gap-4">
        {items.map((item) => (
          <MatchResultCard
            key={item.globalId}
            item={item}
            imageUrl={item.primaryPhotoKey ? (imageUrls?.[item.primaryPhotoKey] ?? null) : null}
            onItemClick={onItemClick}
          />
        ))}
      </div>
    </div>
  );
}

function MatchResultCard({
  item,
  imageUrl,
  onItemClick,
}: {
  item: DecorMatchResponseItem;
  imageUrl: string | null;
  onItemClick: (globalId: string) => void;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = Boolean(imageUrl) && !imgFailed;

  return (
    <button
      type="button"
      onClick={() => onItemClick(item.globalId)}
      aria-label={`Открыть карточку: ${item.name}, ${item.brand}`}
      className={[
        'w-full rounded-xl bg-card border-[1.5px] border-accent shadow-card overflow-hidden',
        'text-left p-0 cursor-pointer flex',
        'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
      ].join(' ')}
    >
      <div
        className="w-24 h-24 bg-icon-bg flex items-center justify-center text-muted text-2xl font-display shrink-0 overflow-hidden"
        aria-hidden="true"
      >
        {showImage ? (
          <img
            src={imageUrl as string}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          item.brand.charAt(0)
        )}
      </div>
      <div className="p-3 flex-1 min-w-0">
        <p className="font-body text-[16px] font-semibold text-ink m-0 line-clamp-2">{item.name}</p>
        <p className="text-muted text-sm m-0 mt-1">{item.brand}</p>
      </div>
    </button>
  );
}
